import { FC, useEffect, useState } from 'react';
import { SummaryViewType } from '../../types/view/SummaryViewType';
import summaryClass from '../../assets/scss/SubscriptionDetail/Summary.module.scss';
import ja from 'date-fns/locale/ja';
import { registerLocale } from 'react-datepicker';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { noDeliveryTimeText } from '../../utils/createChangeDeliveryDateModal';
import { useMasterContext } from '../../context';

registerLocale('ja', ja);
dayjs.locale('ja');

type RecentOrderType = {
  status: string;
  deliveryDate: string;
  deliveryTime: string;
};

export const DeliverySummarySection: FC<{
  summary: SummaryViewType;
  isDeliveryTimeSetting: boolean;
  clickDeliveryDateFunc: () => void;
  clickDeliveryIntervalFunc: () => void;
}> = ({ summary, isDeliveryTimeSetting, clickDeliveryDateFunc, clickDeliveryIntervalFunc }) => {
  const { settings } = useMasterContext();
  const deliveryAttention = settings?.deliveryAttention ?? null;

  const [recentOrder, setRecentOrder] = useState<RecentOrderType | null>(null);
  useEffect(() => {
    if (!summary.recentOrderId) return;

    const orderId = summary.recentOrderId.split('/').pop();
    if (!orderId) return;

    window.parent.postMessage({ action: 'getRecentOrder', orderId }, '*');

    const handleMessage = (event: MessageEvent<any>) => {
      if (event.data.action !== 'recentOrder') return;

      const latestOrder = event.data.matchedOrder;
      if (!latestOrder) return;

      // キャンセル済みは表示しない
      if (latestOrder.status === 'canceled') return;

      // 注文日が不正な場合は表示しない
      const createdAt = dayjs(latestOrder.createdAt);
      if (!createdAt.isValid()) return;

      // 現在時刻 >「注文確定～6日後」なら表示しない
      const now = dayjs();
      const sixDaysLater = createdAt.add(6, 'day');
      if (now.isAfter(sixDaysLater, 'day')) return;

      // 配送日（指定がなければ注文日+2日）
      const deliveryDate = latestOrder.deliveryDate
        ? latestOrder.deliveryDate
        : createdAt.add(2, 'day').format('YYYY-MM-DD');

      setRecentOrder({
        status: latestOrder.status,
        deliveryDate,
        deliveryTime: latestOrder.deliveryTime,
      });
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [summary.recentOrderId]);

  return (
    <div className={summaryClass.deliverySummarySection}>
      {/* === お届けスケジュール === */}
      <div className={summaryClass.deliverySchedule}>
        {/* 直近のお届け */}
        {recentOrder && (
          <div className={summaryClass.recentDeliverySchedule}>
            <span className={summaryClass.deliveryScheduleTitle}>直近のお届け</span>
            {recentOrder.status === 'unfulfilled' ? (
              <span className={summaryClass.recentDeliveryScheduleUnfulfilledStatus}>
                発送準備中
              </span>
            ) : (
              <span className={summaryClass.recentDeliveryScheduleFulfilledStatus}>発送済み</span>
            )}
            <div className={summaryClass.deliveryScheduleDateTime}>
              <span className={summaryClass.deliveryScheduleDate}>
                {dayjs(recentOrder.deliveryDate).format('YYYY/M/D（ddd）')}
              </span>
              <span className={summaryClass.deliveryScheduleTime}>{recentOrder.deliveryTime}</span>
            </div>
          </div>
        )}

        {/* 次回のお届け */}
        <div className={summaryClass.nextDeliverySchedule}>
          <span className={summaryClass.deliveryScheduleTitle}>次回のお届け</span>
          <div className={summaryClass.deliveryScheduleDateTime}>
            <span className={summaryClass.deliveryScheduleDate}>
              {dayjs(summary.nextDeliveryDate).format('YYYY/M/D（ddd）')}
            </span>
            {isDeliveryTimeSetting && (
              <span className={summaryClass.deliveryScheduleTime}>
                {summary.nextDeliveryTime ? summary.nextDeliveryTimeText : noDeliveryTimeText}
              </span>
            )}
          </div>
          <button
            className={summaryClass.nextDeliveryBtn}
            onClick={clickDeliveryDateFunc}
          >
            {isDeliveryTimeSetting ? 'お届け日・時間を変更する' : 'お届け日を変更する'}
          </button>
          <div className={summaryClass.nextBillingSchedule}>
            {dayjs(summary.nextDeliveryDate).startOf('day').subtract(6, 'day').format('YYYY/MM/DD')}
            まで変更可能
          </div>
        </div>
      </div>

      {/* 周期設定 */}
      <div className={summaryClass.deliveryInterval}>
        <span className={summaryClass.interval}>周期：{summary.billingPolicyInterval}</span>
        <button
          className={summaryClass.deliveryIntervalBtn}
          onClick={clickDeliveryIntervalFunc}
        >
          変更
        </button>
      </div>

      {/* 注意書き */}
      {deliveryAttention && (
        <div
          className={summaryClass.deliveryAttention}
          dangerouslySetInnerHTML={{
            __html: isDeliveryTimeSetting
              ? deliveryAttention.packHtml
              : deliveryAttention.packetHtml,
          }}
        />
      )}
    </div>
  );
};
